// @LTG-ID: data/journals/do-i-need-a-business-broker
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 30 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Deal Process (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "do-i-need-a-business-broker",
    FRONTMATTER: {
        number: "67",
        section: "Deal Process",
        sectionHref: "/journals#deal-process",
        sectionAnchor: "deal-process",
        title: "Do I Need a Business Broker to Sell My Business?",
        deck: "A business broker is most valuable when you need to find buyers confidentially, run a competitive process, and keep a deal moving while you run the business.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-06-16",
        publishedHuman: "June 16, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 507,
        timeRequired: "PT3M",
        keywords: "do I need a business broker, selling a business without a broker, business broker fees, business broker vs selling yourself",
        articleSection: "Deal Process",
        articleBodyPreview: "A business broker is most valuable when you need to find buyers confidentially, run a competitive process, and keep a deal moving while you run the business. Selling without one can work when a credible buyer already exists. Broker fees commonly run around ten percent for Main Street businesses, so the question is whether the process value exceeds the fee. The commission is visible; the value is not. That asymmetry makes the broker question one of the most argued topics among sellers, so here is",
        complianceNote: "This article is educational and does not constitute legal or financial advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-long-does-it-take-to-sell-a-business",
        "due-diligence-selling-a-business",
        "sell-business-confidentially"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Do I Need a Business Broker to Sell My Business?",
        canonical: "https://letstalkgrowth.com/journals/do-i-need-a-business-broker",
        ogImage: "/journals/og/do-i-need-a-business-broker.jpg",
    },
    BODY_MARKDOWN: `A business broker is most valuable when you need to find buyers confidentially, run a competitive process, and keep a deal moving while you run the business. Selling without one can work when a credible buyer already exists. Broker fees commonly run around ten percent for Main Street businesses, so the question is whether the process value exceeds the fee.

The commission is visible; the value is not. That asymmetry makes the broker question one of the most argued topics among sellers, so here is the honest version of both sides.

## What a good broker actually does

The public imagines matchmaking. The real work is a process: pricing the business against market evidence so it neither languishes overpriced nor sells cheap, building the confidential marketing described in our confidentiality article, reaching buyer networks an individual seller cannot, qualifying prospects so the serious two emerge from the curious twenty, creating the competitive tension among buyers that does more for price than any negotiation tactic, and then, the underrated part, project-managing the months of diligence, financing, and closing mechanics during which unattended deals quietly die. Meanwhile the owner does the one thing only the owner can do: keep the business performing, because sagging results mid-process reprice deals, as this series has said more than once.

Fees for Main Street businesses commonly land around ten percent of the transaction, with smaller deals sometimes carrying minimum fees and larger deals negotiating downward or using tiered structures; mid-market M&A advisors price differently again. Verify current norms for your size, and read engagement terms, exclusivity periods, tail provisions, carefully with your attorney.

## When selling without a broker genuinely makes sense

The strongest case is a credible buyer already at the table: the manager buying you out, the competitor who has courted you for years, the family successor. When the search function is unnecessary, the fee buys much less, and many owners in that position hire the deal team, an experienced transaction attorney and their CPA, plus perhaps an independent valuation, and skip the intermediary. That is a rational configuration, with one warning attached: single-buyer negotiations lack competitive tension, so the pricing homework must be done independently and the walk-away number known coldly, or the lone buyer's leverage sets the price.

Full self-marketing, finding strangers yourself while running the company and preserving confidentiality, is the route with the worst odds. It can be done, and every broker fee saved is real money, but the seller is taking on a second full-time job whose amateur execution shows up precisely where it costs the most: pricing, screening, and momentum.

## The decision in one paragraph

Known buyer, strong advisors, done pricing homework: proceeding without a broker is defensible and common. No buyer identified, confidentiality essential, business still needs your full attention: the process value of good representation usually exceeds the fee, which is why most Main Street sales use one. Either way, the attorney and the CPA are not optional; the broker question is only about who runs the search.`,
    FAQ: [
        { question: "How much do business brokers charge?", answer: "Commonly around ten percent of the sale price for Main Street businesses, often with minimum fees on smaller deals; larger transactions typically negotiate lower or tiered rates with M&A advisors. Confirm current market norms and scrutinize engagement terms before signing." },
        { question: "Can I sell my business myself?", answer: "Yes, and it works best when a credible buyer already exists, such as a manager, competitor, or family member. Selling to unknown buyers without representation means personally handling confidential marketing, screening, negotiation, and deal management while still running the business." },
        { question: "What should I look for in a business broker?", answer: "A track record of closed deals near your size and industry, a concrete confidential marketing process, transparent fees and engagement terms, and references from recent sellers. Interview more than one; the difference between average and excellent representation is large and visible in the questions they ask you." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Exit Planning",
            title: "How Long Does It Take to Sell a Business?",
            meta: "3 min · Brief 43",
            to: "/journals/how-long-does-it-take-to-sell-a-business",
        },
        {
            tag: "Deal Process",
            title: "What Is Due Diligence When Selling a Business? A Seller's Survival Guide",
            meta: "3 min · Brief 56",
            to: "/journals/due-diligence-selling-a-business",
        },
        {
            tag: "Deal Process",
            title: "How to Sell Your Business Confidentially (Without Employees or Competitors Finding Out)",
            meta: "3 min · Brief 66",
            to: "/journals/sell-business-confidentially",
        },
    ],
};

export default BRIEF;
